import { CheckCircle2, Pencil, CalendarCheck, Download } from "lucide-react";
import { api } from "../../lib/api";
import { PortalAcceptEstimate } from "./PortalAcceptEstimate";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const gbp = (n) => "£" + Math.round(Number(n || 0)).toLocaleString("en-GB");

const VISUALS = [
  ["photo_url", "Exterior", "portal-quote-render"],
  ["interior_photo_url", "Interior", "portal-quote-render-interior"],
  ["kitchen_photo_url", "Kitchen", "portal-quote-render-kitchen"],
  ["bathroom_photo_url", "Bathroom / WC", "portal-quote-render-bathroom"],
];

export function PortalQuoteView({ quote, onLogout }) {
  const total = Math.round(quote?.total_inc_vat || 0);
  const editUrl = quote?.edit_quote_id ? `/quote?edit=${quote.edit_quote_id}` : "/quote";
  const visuals = VISUALS.filter(([k]) => quote?.[k]);
  const est = quote?.estimate;

  return (
    <main data-testid="portal-quote-page" className="px-6 md:px-12 lg:px-16 pt-12 pb-24">
      <div className="flex justify-between items-start mb-8">
        <div className="min-w-0">
          <div className="label-eyebrow">Your saved quote</div>
          <h1 className="font-black tracking-tighter text-3xl sm:text-4xl md:text-5xl break-all" data-testid="portal-quote-reference">{quote.reference}</h1>
          <p className="text-sm text-[#6B7280] mt-2">Valid until <b>{quote.valid_until ? new Date(quote.valid_until).toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" }) : "—"}</b></p>
        </div>
        <button data-testid="portal-logout" onClick={onLogout} className="de-btn-secondary shrink-0 !px-4 !py-2.5 sm:!px-8 sm:!py-[0.95rem]">Sign out</button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          {est && (
            <div className="border-2 border-[#C79A3B] bg-white p-6" data-testid="portal-revised-quote">
              <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
                <div className="label-eyebrow">Your revised quote</div>
                <span className="text-[10px] font-black uppercase tracking-widest bg-[#C79A3B] text-white px-2 py-0.5" data-testid="portal-revised-revision">Revision {est.revision}</span>
              </div>
              <p className="text-xs text-[#6B7280] mb-4">
                Updated by our team{est.updated_at ? ` on ${new Date(est.updated_at).toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" })}` : ""} to reflect exactly what we agreed{quote.route === "pd" ? " — usually after your site visit" : ""}.
              </p>
              <ul className="divide-y divide-[#F0EAE0]">
                {est.line_items.map((li, i) => (
                  <li key={`${i}-${li.name}`} className="flex justify-between gap-3 py-2 text-sm" data-testid={`portal-estimate-line-${i}`}>
                    <span className="text-[#374151]">{li.name}</span>
                    <span className="font-bold tabular-nums text-[#12263A]">{gbp(li.amount)}</span>
                  </li>
                ))}
              </ul>
              <div className="flex justify-between items-center border-t-2 border-[#12263A] mt-2 pt-3">
                <span className="text-xs font-black uppercase tracking-widest text-[#12263A]">Revised total (inc VAT)</span>
                <span className="text-2xl font-black text-[#12263A] tabular-nums" data-testid="portal-estimate-total">{gbp(est.total_inc_vat)}</span>
              </div>
            </div>
          )}
          {visuals.length > 0 && (
            <div className={`grid gap-4 ${visuals.length > 1 ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1"}`} data-testid="portal-quote-visuals">
              {visuals.map(([k, label, tid]) => (
                <figure key={k} className="relative border border-[#E5E7EB] bg-white overflow-hidden">
                  <img src={`${BACKEND}${quote[k]}`} alt={`Your extension — ${label.toLowerCase()} visual`} loading="lazy"
                    onError={(e) => { e.target.closest("figure").style.display = "none"; }}
                    className="w-full h-auto bg-[#F4F4F4]" data-testid={tid} />
                  <figcaption className="absolute top-3 left-3 bg-[#12263A]/90 text-white text-[10px] font-bold uppercase tracking-widest px-2.5 py-1">{label}</figcaption>
                </figure>
              ))}
            </div>
          )}
          {quote.route === "pd" && (quote.spec_lines || []).length > 0 && (
            <div className="border border-[#E5E7EB] bg-white p-6">
              <div className="label-eyebrow mb-3">Your specification</div>
              <ul className="space-y-2" data-testid="portal-quote-spec">
                {quote.spec_lines.map((l, i) => (
                  <li key={`${i}-${l}`} className="flex gap-2 text-sm text-[#374151]"><CheckCircle2 size={16} className="text-[#C79A3B] mt-0.5 shrink-0" /><span>{l}</span></li>
                ))}
              </ul>
            </div>
          )}
          {(quote.included_list || []).length > 0 && (
            <div className="border border-[#E5E7EB] bg-white p-6">
              <div className="label-eyebrow mb-3">Always included</div>
              <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2">
                {quote.included_list.map((l, i) => (
                  <li key={`${i}-${l}`} className="flex gap-2 text-sm text-[#6B7280]"><CheckCircle2 size={15} className="text-[#9CA3AF] mt-0.5 shrink-0" /><span>{l}</span></li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div>
          <div className="bg-[#12263A] text-white p-6 sticky top-6">
            <div className="text-xs uppercase tracking-widest text-[#C79A3B]">{est ? "Revised total (incl. VAT)" : "Total (incl. VAT)"}</div>
            <div className="text-4xl font-black mt-1" data-testid="portal-quote-total">£{total.toLocaleString("en-GB")}</div>
            {est ? (
              <>
                <div className="text-xs text-white/70 mt-2">This quote was tailored for you by our team. Ready to go ahead? Accept below and reserve your build date.</div>
                <PortalAcceptEstimate quote={quote} />
                <div className="mt-3">
                  <a href={`${api.defaults.baseURL}/portal/quote-pdf`} target="_blank" rel="noreferrer"
                    data-testid="portal-quote-download" className="de-btn-secondary w-full justify-center bg-white">
                    <Download size={15} /> Download quote PDF
                  </a>
                </div>
                <p className="text-[10px] text-white/50 mt-3">Need a change to this quote? Call the office — it was priced specifically for your project.</p>
              </>
            ) : (
              <>
                <div className="text-xs text-white/70 mt-2">This quote is a reference for your saved design. Final sizes &amp; cost are confirmed at survey.</div>
                <div className="mt-5 space-y-2">
                  <a href={editUrl} data-testid="portal-quote-book" className="de-btn-primary w-full justify-center"><CalendarCheck size={16} /> Book a date &amp; pay £499</a>
                  <a href={editUrl} data-testid="portal-quote-edit" className="de-btn-secondary w-full justify-center bg-white"><Pencil size={15} /> Edit my quote</a>
                  <a href={`${api.defaults.baseURL}/portal/quote-pdf`} target="_blank" rel="noreferrer"
                    data-testid="portal-quote-download" className="de-btn-secondary w-full justify-center bg-white">
                    <Download size={15} /> Download quote PDF
                  </a>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}
